import mixpanel from 'mixpanel-browser';
import constants from '@core/utils/constants';

const MIXPANEL_TOKEN = 'e70c708a0b1f89af38659622522dfcd1';
const MIXPANEL_DEBUG = false;

function isCapacitorEnv() {
  if (typeof window === 'undefined') return false;
  const href = window.location.href || '';
  const hasParent = window.self !== window.top;
  return hasParent || /capacitor/i.test(href);
}

function requestPlatformFromParent(timeout = 1000) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve('web');
    let timeoutId = null;

    const handler = (event) => {
      if (event.data?.type === 'APP_PLATFORM_RESPONSE') {
        clearTimeout(timeoutId);
        window.removeEventListener('message', handler);
        resolve(event.data.platform || 'web');
      }
    };

    window.addEventListener('message', handler);

    timeoutId = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, timeout);

    try {
      window.parent.postMessage({ type: 'REQUEST_APP_PLATFORM' }, '*');
    } catch (e) {
      clearTimeout(timeoutId);
      window.removeEventListener('message', handler);
      resolve(null);
    }
  });
}

async function resolveAppPlatform() {
  if (!isCapacitorEnv()) {
    return 'web';
  }

  // Si on est dans le contexte Capacitor (iframe ou script natif)
  const nativePlatform = await requestPlatformFromParent(800);
  if (nativePlatform && nativePlatform !== 'web') {
    return nativePlatform; // 'ios' ou 'android'
  }

  // Fallback heuristique si le parent n'a pas répondu à temps
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';

  return 'web';
}

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.token = MIXPANEL_TOKEN || '';
    this.debug = MIXPANEL_DEBUG === 'true';
    this.platform = 'web';
    this.isCapacitor = false;
  }

  async init(router = null) {
    if (!this.token) {
      console.warn('[Analytics] Mixpanel token missing (MIXPANEL_TOKEN). Tracking will run in stub/debug mode.');
    }

    this.isCapacitor = isCapacitorEnv();
    this.platform = await resolveAppPlatform();

    try {
      if (this.token) {
        const initOptions = {
          debug: this.debug,
          track_pageview: false, // Page views gérées manuellement via router
          persistence: 'localStorage',
          ignore_dnt: true
        };

        // Si on est dans Capacitor (iOS / Android / iframe), bloquer totalement $current_url au niveau SDK
        if (this.isCapacitor) {
          initOptions.property_blacklist = ['$current_url'];
        }

        mixpanel.init(this.token, initOptions);

        // Enregistrer la plateforme sur TOUS les événements Mixpanel
        mixpanel.register({
          platform: this.platform,
        });
      }
      this.isInitialized = true;
      console.log(`[Analytics] Mixpanel initialisé avec succès (platform: ${this.platform})`);
    } catch (err) {
      console.error('[Analytics] Erreur initialisation Mixpanel:', err);
    }

    if (router) {
      this.setupRouterTracking(router);
    }
  }

  identify(user) {
    if (!user || !user.id) return;

    try {
      const fullName =
        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.name ||
        user.username ||
        user.displayName ||
        undefined;

      const userEmail = user.email || undefined;
      const userPhone = user.phoneNumber || user.phone || undefined;

      if (this.isInitialized && this.token) {
        mixpanel.identify(user.id);

        const userProps = {
          $name: fullName,
          name: fullName,
          $email: userEmail,
          email: userEmail,
          $phone: userPhone,
          phone: userPhone,
          city: user.city || user.address?.city || undefined,
          role: user.role || 'customer'
        };

        // Supprimer les clés non définies
        Object.keys(userProps).forEach((key) => userProps[key] === undefined && delete userProps[key]);

        mixpanel.people.set(userProps);

        // Enregistrer également sur les événements futurs (super properties)
        const superProps = {};
        if (fullName) superProps.userName = fullName;
        if (userEmail) superProps.userEmail = userEmail;
        if (userPhone) superProps.userPhone = userPhone;
        if (Object.keys(superProps).length > 0) {
          mixpanel.register(superProps);
        }
      }
      if (this.debug) {
        console.log('[Analytics] Identify:', user.id, user);
      }
    } catch (err) {
      console.error('[Analytics] Erreur identify:', err);
    }
  }

  reset() {
    try {
      if (this.isInitialized && this.token) {
        mixpanel.reset();
      }
      if (this.debug) {
        console.log('[Analytics] Reset identity');
      }
    } catch (err) {
      console.error('[Analytics] Erreur reset identity:', err);
    }
  }

  track(eventName, properties = {}) {
    const payload = {
      ...properties,
      platform: this.platform,
      timestamp: new Date().toISOString()
    };

    // Si on est dans Capacitor/webview (pas la vraie version web sans parent), on n'envoie pas le current_url
    if (this.isCapacitor) {
      payload.$current_url = undefined;
    }

    try {
      if (this.isInitialized && this.token) {
        mixpanel.track(eventName, payload);
      }
      if (this.debug) {
        console.log(`[Analytics Event] "${eventName}":`, payload);
      }
    } catch (err) {
      console.error(`[Analytics] Erreur tracking "${eventName}":`, err);
    }
  }

  setupRouterTracking(router) {
    router.afterEach((to, from) => {
      const pageProps = {
        page: to.name || to.path,
        path: to.path,
        from: from?.path || null
      };

      // Le current_url n'est présent que sur la vraie version web (pas de parent et pas isCapacitor)
      if (!this.isCapacitor) {
        pageProps.$current_url = window.location.href;
      }

      this.track('Page Viewed', pageProps);
    });
  }

  setupBusListeners(bus) {
    if (!bus) return;

    // --- AUTH EVENTS ---
    bus.on(constants.EVENTS.USER.LOGIN, (data) => {
      this.track('Login Attempted', {
        method: data?.email ? 'email' : (data?.phone || data?.phoneNumber ? 'phone' : 'unknown')
      });
    });

    bus.on(constants.EVENTS.USER.LOGIN_SUCCESS, (data) => {
      const user = data?.user || data;
      this.identify(user);
      const fullName =
        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
        user?.name ||
        user?.username ||
        undefined;
      this.track('Login Success', {
        userId: user?.id,
        email: user?.email,
        name: fullName,
        phone: user?.phoneNumber || user?.phone
      });
    });

    bus.on(constants.EVENTS.USER.LOGIN_ERROR, (error) => {
      this.track('Login Failed', {
        error: error?.message || error
      });
    });

    bus.on(constants.EVENTS.USER.REGISTER, () => {
      this.track('Register Attempted');
    });

    bus.on(constants.EVENTS.USER.REGISTER_SUCCESS, (data) => {
      const user = data?.user || data;
      this.identify(user);
      const fullName =
        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
        user?.name ||
        user?.username ||
        undefined;
      this.track('Register Success', {
        userId: user?.id,
        email: user?.email,
        name: fullName,
        phone: user?.phoneNumber || user?.phone
      });
    });

    bus.on(constants.EVENTS.USER.REGISTER_ERROR, (error) => {
      this.track('Register Failed', {
        error: error?.message || error
      });
    });

    bus.on(constants.EVENTS.USER.LOGOUT, () => {
      this.track('Logout');
      this.reset();
    });

    bus.on(constants.EVENTS.USER.UPDATE_SUCCESS, (user) => {
      this.identify(user);
      this.track('Profile Updated');
    });

    // --- RESTAURANTS & BROWSING ---
    if (constants.EVENTS.RESTAURANTS?.LIST_SUCCESS) {
      bus.on(constants.EVENTS.RESTAURANTS.LIST_SUCCESS, (data) => {
        const count = Array.isArray(data) ? data.length : (data?.data?.length || 0);
        this.track('Restaurants Listed', { count });
      });
    }

    if (constants.EVENTS.RESTAURANTS?.DETAIL_SUCCESS) {
      bus.on(constants.EVENTS.RESTAURANTS.DETAIL_SUCCESS, (rest) => {
        this.track('Restaurant Viewed', {
          restaurantId: rest?.id || rest?._id,
          name: rest?.name
        });
      });
    }

    // --- CART EVENTS ---
    bus.on(constants.EVENTS.CART?.SHOW || 'cart.show', () => {
      this.track('Cart Opened');
    });

    bus.on(constants.EVENTS.CART?.HIDE || 'cart.hide', () => {
      this.track('Cart Closed');
    });

    bus.on(constants.EVENTS.CART?.ITEMS?.ADD || 'cart.items.add', (payload) => {
      this.track('Product Added to Cart', {
        dishId: payload?.id || payload?.dishId,
        name: payload?.name || payload?.title,
        price: payload?.price,
        quantity: payload?.quantity || 1,
        restaurantId: payload?.restaurantId
      });
    });

    bus.on(constants.EVENTS.CART?.ITEMS?.REMOVE || 'cart.items.remove', (payload) => {
      this.track('Product Removed from Cart', {
        itemId: payload?.itemId || payload?.id,
        dishId: payload?.dishId
      });
    });

    bus.on(constants.EVENTS.CART?.ITEMS?.UPDATE || 'cart.items.update', (payload) => {
      this.track('Cart Item Updated', {
        itemId: payload?.itemId,
        quantity: payload?.quantity
      });
    });

    bus.on(constants.EVENTS.CART?.CLEAR || 'cart.clear', () => {
      this.track('Cart Cleared');
    });

    bus.on(constants.EVENTS.CART?.COUPON?.APPLY || 'cart.coupon.apply', (data) => {
      this.track('Coupon Applied', {
        couponCode: data?.code || data?.couponCode
      });
    });

    bus.on(constants.EVENTS.CART?.COUPON?.APPLIED || 'cart.coupon.applied', (res) => {
      this.track('Coupon Apply Success', {
        discount: res?.discount || res?.amount
      });
    });

    bus.on(constants.EVENTS.CART?.COUPON?.APPLY_ERROR || 'cart.coupon.apply_error', (err) => {
      this.track('Coupon Apply Failed', {
        error: err?.message || err
      });
    });

    bus.on(constants.EVENTS.CART?.ORDER_FEES_CALCULATED || 'cart.order_fees.calculated', (fees) => {
      this.track('Fees Calculated', {
        subtotal: fees?.subtotal,
        deliveryFee: fees?.deliveryFee,
        total: fees?.total
      });
    });

    // --- ORDERS & CHECKOUT FUNNEL ---
    bus.on(constants.EVENTS.ORDERS?.CREATE || 'orders.create', (orderData) => {
      this.track('Order Initiated', {
        restaurantId: orderData?.cartData?.restaurantId || orderData?.restaurantId,
        paymentMethod: orderData?.paymentData?.method || orderData?.paymentMethod,
        total: orderData?.total || orderData?.pricing?.total,
        itemCount: orderData?.cartData?.items?.length || 0
      });
    });

    bus.on(constants.EVENTS.ORDERS?.CREATED || 'orders.created', (createdOrder) => {
      const orderId = createdOrder?.id || createdOrder?._id;
      const total = createdOrder?.totalPrice || createdOrder?.total;
      this.track('Order Placed', {
        orderId,
        total,
        paymentMethod: createdOrder?.paymentMethod || createdOrder?.payment?.method,
        restaurantId: createdOrder?.restaurantId,
        itemCount: createdOrder?.items?.length || 0
      });
    });

    bus.on(constants.EVENTS.ORDERS?.CREATE_ERROR || 'orders.create_error', (error) => {
      this.track('Order Failed', {
        error: error?.message || error
      });
    });

    bus.on(constants.EVENTS.ORDERS?.CANCEL || 'orders.cancel', (orderId) => {
      this.track('Order Cancel Requested', { orderId });
    });

    bus.on(constants.EVENTS.ORDERS?.CANCELLED || 'orders.cancelled', (orderId) => {
      this.track('Order Cancelled', { orderId });
    });

    bus.on(constants.EVENTS.ORDERS?.RATE || 'orders.rate', (data) => {
      this.track('Order Rating Submitted', {
        orderId: data?.orderId,
        rating: data?.rating
      });
    });

    // --- FAVORITES ---
    bus.on(constants.EVENTS.USER?.FAVORITE_TOGGLE_REST || 'user.favorite_toggle_rest', (data) => {
      this.track('Restaurant Favorite Toggled', {
        restaurantId: data?.restaurantId,
        isFavorite: data?.isFavorite
      });
    });

    bus.on(constants.EVENTS.USER?.FAVORITE_TOGGLE_DISH || 'user.favorite_toggle_dish', (data) => {
      this.track('Dish Favorite Toggled', {
        dishId: data?.dishId,
        isFavorite: data?.isFavorite
      });
    });

    // --- WALLET ---
    if (constants.EVENTS.WALLET?.DEPOSIT) {
      bus.on(constants.EVENTS.WALLET.DEPOSIT, (data) => {
        this.track('Wallet Deposit Initiated', { amount: data?.amount, provider: data?.provider });
      });
    }
    if (constants.EVENTS.WALLET?.DEPOSIT_SUCCESS) {
      bus.on(constants.EVENTS.WALLET.DEPOSIT_SUCCESS, (data) => {
        this.track('Wallet Deposit Success', { amount: data?.amount });
      });
    }
    if (constants.EVENTS.WALLET?.WITHDRAW) {
      bus.on(constants.EVENTS.WALLET.WITHDRAW, (data) => {
        this.track('Wallet Withdraw Initiated', { amount: data?.amount, provider: data?.provider });
      });
    }
    if (constants.EVENTS.WALLET?.WITHDRAW_SUCCESS) {
      bus.on(constants.EVENTS.WALLET.WITHDRAW_SUCCESS, (data) => {
        this.track('Wallet Withdraw Success', { amount: data?.amount });
      });
    }

    // --- APP READY & USER SYNC ---
    bus.on(constants.EVENTS.APP?.READY || 'app.ready', () => {
      this.track('App Ready');
    });
  }
}

export const analytics = new AnalyticsService();
export default analytics;
